import { useEffect, useState } from "react";
import { ArrowLeft, Image as ImageIcon, UtensilsCrossed } from "lucide-react";

interface ArticuloVisual {
  id: number;
  codigo: number;
  nombre: string;
  precio: number;
  fotoUrl?: string | null;
  descripcion?: string | null;
  etiquetas: string[];
  destacado: boolean;
  agotado: boolean;
}
interface CategoriaConArticulos {
  id: number;
  nombre: string;
  descripcion?: string | null;
  fotoUrl?: string | null;
  articulos: ArticuloVisual[];
}
interface Catalogo {
  sucursalId: number;
  modo: "menu" | "delivery";
  categorias: CategoriaConArticulos[];
}

export function CatalogoVisual({
  sucursalId, color, onVolver,
}: {
  sucursalId: number;
  color: string;
  onVolver: () => void;
}) {
  const [data, setData] = useState<Catalogo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [catSel, setCatSel] = useState<number | null>(null);

  useEffect(() => {
    setCargando(true);
    fetch(`/api/v1/menu-publico/sucursal/${sucursalId}/catalogo?modo=menu`)
      .then((r) => r.json())
      .then((d: Catalogo) => {
        setData(d);
        if (d?.categorias?.length) setCatSel(d.categorias[0].id);
      })
      .catch(() => setData({ sucursalId, modo: "menu", categorias: [] }))
      .finally(() => setCargando(false));
  }, [sucursalId]);

  const cats = data?.categorias ?? [];
  const catActiva = cats.find((c) => c.id === catSel) ?? cats[0];

  return (
    <div className="pb-32">
      {/* Barra superior */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 shadow-sm">
        <button onClick={onVolver} className="p-1.5 rounded-full hover:bg-gray-100">
          <ArrowLeft className="size-5 text-gray-600" />
        </button>
        <p className="font-bold uppercase tracking-wide text-gray-900 text-sm flex-1"
          style={{ fontFamily: "Georgia, serif" }}>
          Menú visual
        </p>
      </div>

      {cargando ? (
        <div className="px-3 py-6 space-y-4">
          {/* Skeleton de carga */}
          {[1,2,3].map(i => (
            <div key={i} className="space-y-3">
              <div className="skeleton h-5 w-1/3" />
              <div className="grid grid-cols-2 gap-3">
                <div className="skeleton h-44 rounded-xl" />
                <div className="skeleton h-44 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      ) : cats.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mb-3" aria-hidden>
            <UtensilsCrossed size={32} strokeWidth={1.6} />
          </span>
          <p>Aún no hay categorías configuradas para el menú.</p>
        </div>
      ) : (
        <>
          {/* Pestañas de categoría (scroll horizontal) */}
          <div className="px-3 py-3 bg-white border-b border-gray-100 overflow-x-auto sticky top-12 z-20">
            <div className="flex gap-2 min-w-min">
              {cats.map((c) => {
                const activa = c.id === catActiva?.id;
                return (
                  <button key={c.id} onClick={() => setCatSel(c.id)}
                    className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
                      activa ? "text-white border-transparent" : "bg-white text-gray-700 border-gray-300"
                    }`}
                    style={activa ? { backgroundColor: color, borderColor: color } : {}}>
                    {c.nombre}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lista de artículos de la categoría activa */}
          <div className="px-4 pt-4 max-w-2xl mx-auto">
            {catActiva?.descripcion && (
              <p className="text-sm text-gray-500 mb-3">{catActiva.descripcion}</p>
            )}
            <div className="space-y-3">
              {catActiva?.articulos.map((art) => (
                <div key={art.id}
                  className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex ${art.agotado ? "opacity-60" : ""}`}>
                  {art.fotoUrl ? (
                    <img src={art.fotoUrl} alt={art.nombre}
                      className="w-28 h-28 sm:w-32 sm:h-32 object-cover shrink-0" />
                  ) : (
                    <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gray-50 flex items-center justify-center shrink-0 text-gray-300">
                      <ImageIcon className="size-8" />
                    </div>
                  )}
                  <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight">
                          {art.nombre}
                          {art.destacado && (
                            <span className="ml-1 text-yellow-500" title="Destacado">⭐</span>
                          )}
                        </h4>
                        <span className="font-bold text-gray-900 text-sm sm:text-base whitespace-nowrap"
                          style={{ color }}>
                          ${art.precio.toFixed(2)}
                        </span>
                      </div>
                      {art.descripcion && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-3">{art.descripcion}</p>
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {art.agotado && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium">
                          Agotado
                        </span>
                      )}
                      {art.etiquetas.map((e, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {catActiva?.articulos.length === 0 && (
                <p className="text-center text-gray-400 py-8 text-sm">
                  Sin artículos en esta categoría.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
